import axios from 'axios';
import apiUrl from '../constants';
import commonFunction from '../commonFunction';
import constantVariable from '../constantVariable';

/**
 * Direct Appointment API endpoints.
 * Endpoints marked [BACKEND REQUIRED] must be implemented on the CRM backend.
 */
const API_ENDPOINTS = {
  // Reused from Cargo Appointment Date module
  GET_CLIENT_LIST: 'getClientList',
  GET_ALL_CLIENT_LIST: 'getAllClientList',
  GET_HUB_DETAIL_BY_ID: 'getHubDetailById',
  GET_CARGO_SHIPMENT_DATA: 'getCargoShipmentData',
  SAVE_BULK_SHIPMENT_APPOINTMENT: 'saveBulkShipmentAppointment',

  // [BACKEND REQUIRED] Direct Appointment specific endpoints
  GET_DIRECT_APPOINTMENT_STATUS_LIST: 'getDirectAppointmentStatusList',
  GET_DIRECT_APPOINTMENT_TAGGED_BY_LIST: 'getDirectAppointmentTaggedByList',
  GET_REPORTING_PERSON_LIST: 'getReportingPersonList',
  VALIDATE_DIRECT_APPOINTMENT_UPLOAD: 'validateDirectAppointmentUpload',
  SAVE_DIRECT_APPOINTMENT_BULK: 'saveDirectAppointmentBulk',
};

const getAuthHeaders = () => commonFunction.getusertoken();

const post = (endpoint, data, responseType) => {
  var config = {
    method: 'POST',
    url: apiUrl.api_url + endpoint,
    headers: getAuthHeaders(),
    data: data,
  };
  if (responseType) {
    config.responseType = responseType;
  }
  return axios(config);
};

const postBlob = (endpoint, data) => {
  return post(endpoint, data, 'blob');
};

const postFormData = (endpoint, formData) => {
  return axios({
    method: 'POST',
    url: apiUrl.api_url + endpoint,
    headers: getAuthHeaders(),
    data: formData,
  });
};

const mapClientRecord = (client) => {
  if (!client) {
    return null;
  }

  const value =
    client.ClientBusinessAccountId ||
    client.ClientMasterID ||
    client.value;
  const text =
    client.AccountName ||
    client.CompanyName ||
    client.text;

  if (value === undefined || value === null || value === '' || !text) {
    return null;
  }

  return {
    value: String(value),
    text: text,
  };
};

const extractClientArray = (payload) => {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload.clients)) {
    return payload.clients;
  }
  if (Array.isArray(payload.client)) {
    return payload.client;
  }
  if (Array.isArray(payload.userclient)) {
    return payload.userclient;
  }
  return [];
};

const mapClientsToDropdown = (clients) => {
  const mapped = [];
  const seen = {};

  extractClientArray(clients).forEach((client) => {
    const option = mapClientRecord(client);
    if (option && !seen[option.value]) {
      seen[option.value] = true;
      mapped.push(option);
    }
  });

  return mapped;
};

const mapDropdownRecord = (item) => {
  if (!item) {
    return null;
  }

  const value =
    item.value !== undefined && item.value !== null
      ? item.value
      : item.id || item.userid || item.UserId || item.status || item.Status || item.code;
  const text =
    item.text ||
    item.name ||
    item.username ||
    item.UserName ||
    item.status ||
    item.Status ||
    item.label;

  if (value === undefined || value === null || value === '' || !text) {
    return null;
  }

  return {
    value: String(value),
    text: String(text),
  };
};

const mapDropdownResponse = (result) => {
  const responseData = result && result.data;
  let rows = [];

  if (Array.isArray(responseData)) {
    rows = responseData;
  } else if (responseData && Array.isArray(responseData.data)) {
    rows = responseData.data;
  } else if (responseData && responseData.data && Array.isArray(responseData.data.data)) {
    rows = responseData.data.data;
  } else if (
    responseData &&
    responseData.data &&
    responseData.data.data &&
    Array.isArray(responseData.data.data.data)
  ) {
    rows = responseData.data.data.data;
  } else if (responseData && responseData.data && Array.isArray(responseData.data.results)) {
    rows = responseData.data.results;
  } else if (
    responseData &&
    responseData.data &&
    responseData.data.data &&
    Array.isArray(responseData.data.data.results)
  ) {
    rows = responseData.data.data.results;
  }

  const mapped = [];
  const seen = {};

  rows.forEach((row) => {
    const option = mapDropdownRecord(row);
    if (option && !seen[option.value]) {
      seen[option.value] = true;
      mapped.push(option);
    }
  });

  return mapped;
};

const withDefaultOption = (options, defaultText) => {
  const list = Array.isArray(options) ? options.slice() : [];
  if (!list.length || list[0].value !== '') {
    list.unshift({ value: '', text: defaultText || 'Select' });
  }
  return list;
};

const getAppointmentTaggedByFromRecord = (row) => {
  if (!row) {
    return '';
  }

  return String(
    row.AppointmentTaggedBy ||
    row.appointmenttaggedby ||
    row.appointmentTaggedBy ||
    row.taggedBy ||
    row.taggedby ||
    ''
  ).trim();
};

export default {
  API_ENDPOINTS,
  mapClientsToDropdown,

  getClientListFromStorage() {
    try {
      const stored = JSON.parse(window.localStorage.getItem('userclientList') || '[]');
      return mapClientsToDropdown(
        (stored || []).filter((client) => client.CompanyName && client.CompanyName !== 'ALL')
      );
    } catch (error) {
      return [];
    }
  },

  mapClientListResponse(result) {
    const responseData = result && result.data;

    if (Array.isArray(responseData) && responseData.length && responseData[0].value !== undefined) {
      return mapClientsToDropdown(responseData);
    }

    const nested =
      responseData &&
      responseData.data &&
      responseData.data.data &&
      responseData.data.data.data;

    return mapClientsToDropdown(nested);
  },

  getClientList() {
    return post(API_ENDPOINTS.GET_CLIENT_LIST, {
      BusinessFlow: 'Forward',
      BusinessService: 'Delivery',
    });
  },

  getAllClientList() {
    return post(API_ENDPOINTS.GET_ALL_CLIENT_LIST, {});
  },

  getHubDetailById(hubId) {
    return post(API_ENDPOINTS.GET_HUB_DETAIL_BY_ID, { id: hubId });
  },

  /**
   * Reuses Cargo Appointment Date list API.
   */
  getCargoShipmentData(searchData) {
    return post(API_ENDPOINTS.GET_CARGO_SHIPMENT_DATA, searchData);
  },

  /**
   * Reuses Cargo Appointment Date bulk add/update save API.
   */
  saveBulkShipmentAppointment(payload) {
    return post(API_ENDPOINTS.SAVE_BULK_SHIPMENT_APPOINTMENT, payload);
  },

  buildStatusOptionsFromRecords(records) {
    var seen = {};
    var options = [{ value: '', text: 'Select Status' }];

    (records || []).forEach(function (row) {
      var status = String(row.status || row.shipmentstatus || '').trim();
      if (status && !seen[status]) {
        seen[status] = true;
        options.push({ value: status, text: status });
      }
    });

    return options;
  },

  getAppointmentTaggedByFromRecord(row) {
    return getAppointmentTaggedByFromRecord(row);
  },

  buildTaggedByOptionsFromRecords(records) {
    var seen = {};
    var options = [{ value: '', text: 'Select Tagged By' }];

    (records || []).forEach(function (row) {
      var taggedBy = getAppointmentTaggedByFromRecord(row);
      if (taggedBy && !seen[taggedBy]) {
        seen[taggedBy] = true;
        options.push({ value: taggedBy, text: taggedBy });
      }
    });

    return options;
  },

  /**
   * [BACKEND REQUIRED]
   * Status filter dropdown for Direct Appointment search.
   */
  getDirectAppointmentStatusList() {
    return post(API_ENDPOINTS.GET_DIRECT_APPOINTMENT_STATUS_LIST, {});
  },

  mapStatusListResponse(result) {
    const mapped = mapDropdownResponse(result);
    if (mapped.length) {
      return withDefaultOption(mapped, 'Select Status');
    }
    return constantVariable.DIRECT_APPOINTMENT_STATUS_OPTIONS.slice();
  },

  /**
   * [BACKEND REQUIRED]
   * Tagged By filter dropdown for Direct Appointment search.
   */
  // getDirectAppointmentTaggedByList() {
  //   return post(API_ENDPOINTS.GET_DIRECT_APPOINTMENT_TAGGED_BY_LIST, {});
  // },

  getReportingPersonList(userId) {
    return post(API_ENDPOINTS.GET_REPORTING_PERSON_LIST, { userid: userId });
  },

  mapTaggedByListResponse(result, currentUser) {
    const mapped = mapDropdownResponse(result);
    if (mapped.length) {
      return withDefaultOption(mapped, 'Select Tagged By');
    }

    const fallback = [];
    if (currentUser && currentUser.userid) {
      fallback.push({
        value: String(currentUser.userid),
        text: currentUser.username || String(currentUser.userid),
      });
    }
    return withDefaultOption(fallback, 'Select Tagged By');
  },

  mapReportingPersonListResponse(result, currentUser) {
    const responseData = result && result.data;
    let rows = [];

    if (responseData && responseData.data && responseData.data.data && Array.isArray(responseData.data.data.results)) {
      rows = responseData.data.data.results;
    } else {
      rows = mapDropdownResponse(result).map((item) => item.value);
    }

    const mapped = [];
    const seen = {};

    rows.forEach((row) => {
      let option = null;
      if (typeof row === 'string' || typeof row === 'number') {
        option = { value: String(row), text: String(row) };
      } else {
        option = mapDropdownRecord(row);
      }
      if (option && !seen[option.value]) {
        seen[option.value] = true;
        mapped.push(option);
      }
    });

    if (currentUser && currentUser.userid) {
      const currentValue = String(currentUser.userid);
      if (!seen[currentValue]) {
        mapped.unshift({
          value: currentValue,
          text: currentUser.username || currentValue,
        });
      }
    }

    return withDefaultOption(mapped, 'Select Tagged By');
  },

  /**
   * [BACKEND REQUIRED]
   * Validate uploaded records (business rules: AWB eligibility, holidays, duplicate appointment, etc.).
   * Accepts parsed row payload after frontend structural validation.
   */
  // validateDirectAppointmentUpload(payload) {
  //   return post(API_ENDPOINTS.VALIDATE_DIRECT_APPOINTMENT_UPLOAD, payload);
  // },

  /**
   * [BACKEND REQUIRED]
   * Parse and validate an Excel upload when client-side parsing is unavailable.
   */
  // validateDirectAppointmentUploadFile(formData) {
  //   return postFormData(API_ENDPOINTS.VALIDATE_DIRECT_APPOINTMENT_UPLOAD, formData);
  // },

  /**
   * [BACKEND REQUIRED]
   * Persist validated direct appointment records.
   */
  saveDirectAppointmentBulk(payload) {
    return post(API_ENDPOINTS.SAVE_DIRECT_APPOINTMENT_BULK, payload);
  },
};



// WEBPACK FOOTER //
// ./src/services/directAppointment.service.js